/**
 * Consultation Popout Module
 * Slide-out panel from right; opens on .book_consultation click.
 * Location iframes are lazy-loaded when a location is selected.
 */
class ConsultationPopout {
  constructor() {
    this.isActive = false;
    this.popout = document.querySelector(".consultation-popout");
    this.overlay = document.querySelector(".consultation-popout-overlay");
    this.closeBtn = this.popout
      ? this.popout.querySelector(".close")
      : null;
    this.locationsContainer = this.popout
      ? this.popout.querySelector(".locations")
      : null;
    this.iframesContainer = this.popout
      ? this.popout.querySelector(".iframes")
      : null;

    this.init();
  }

  init() {
    if (!this.popout) {
      // if (this.isDebugEnabled()) {
      //   console.warn("[CONSULTATION POPOUT] Popout element not found");
      // }
      return;
    }

    this.popout.setAttribute("aria-hidden", "true");
    this.bindEvents();

    // if (this.isDebugEnabled()) {
    //   console.log("[CONSULTATION POPOUT] Initialized");
    // }
  }

  isDebugEnabled() {
    return typeof sojTheme !== "undefined" && sojTheme.debug;
  }

  bindEvents() {
    // Open: any .book_consultation button (site-wide) – capture so we run first and can stop propagation
    document.addEventListener(
      "click",
      (e) => {
        const trigger = e.target.closest(".book_consultation");
        if (trigger) {
          e.preventDefault();
          e.stopPropagation();
          const locationKey = trigger.getAttribute("data-location-key");
          this.open(locationKey);
        }
      },
      true
    );

    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.close();
      });
    }

    if (this.overlay) {
      this.overlay.addEventListener("click", (e) => {
        e.preventDefault();
        this.close();
      });
    }

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isActive) {
        this.close();
      }
    });

    // Location buttons: switch location and lazy-load iframes (delegate from document so we always hit the right popout)
    document.addEventListener("click", (e) => {
      if (!this.popout || !this.popout.contains(e.target)) return;
      const locationEl = e.target.closest(".location[data-location-key]");
      if (!locationEl || !this.locationsContainer || !this.locationsContainer.contains(locationEl)) return;
      e.preventDefault();
      e.stopPropagation();
      const key = locationEl.getAttribute("data-location-key");
      if (key === null) return;
      this.showLocation(key);
    });
  }

  open(locationKey) {
    // Close mobile menu if open (only one panel at a time)
    const mobileMenu = typeof window.SOJ !== "undefined" && window.SOJ.getMobileMenu();
    if (mobileMenu && mobileMenu.isActive) {
      mobileMenu.close();
    }

    // Save viewport scroll before overlay changes layout / overflow on `<html>`
    const scrollY =
      typeof window.SOJ_SMOOTH_SCROLL !== "undefined" && window.SOJ_SMOOTH_SCROLL.getScrollPosition
        ? window.SOJ_SMOOTH_SCROLL.getScrollPosition()
        : window.scrollY ?? document.documentElement.scrollTop;

    // Pop-out open may shift overflow; SOJ_SMOOTH_SCROLL + native fallback keep scroll position
    document.dispatchEvent(new CustomEvent("consultationPopoutOpen"));

    document.documentElement.classList.add("consultation-popout-active");
    this.popout.classList.add("is-open");
    this.popout.setAttribute("aria-hidden", "false");
    this.isActive = true;

    if (locationKey !== undefined && locationKey !== null && locationKey !== "") {
      this.showLocation(locationKey);
    } else {
      // Clear active location so user must pick again (no iframes shown until they click)
      this.clearActiveLocation();
    }

    // Preserve scroll across overflow / modal layout changes on the `<html>` root
    const restore = () => {
      if (typeof window.SOJ_SMOOTH_SCROLL !== "undefined" && window.SOJ_SMOOTH_SCROLL.scrollTo) {
        window.SOJ_SMOOTH_SCROLL.scrollTo(scrollY, { immediate: true, force: true });
      }
      window.scrollTo(0, scrollY);
    };
    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });

    // if (this.isDebugEnabled()) {
    //   console.log("[CONSULTATION POPOUT] Opened");
    // }
  }

  close() {
    document.documentElement.classList.remove("consultation-popout-active");
    this.popout.classList.remove("is-open");
    this.popout.setAttribute("aria-hidden", "true");
    this.isActive = false;

    if (
      this.popout === document.activeElement ||
      this.popout.contains(document.activeElement)
    ) {
      document.body.focus();
    }

    document.dispatchEvent(new CustomEvent("consultationPopoutClose"));

    // if (this.isDebugEnabled()) {
    //   console.log("[CONSULTATION POPOUT] Closed");
    // }
  }

  clearActiveLocation() {
    if (!this.popout) return;
    const wrappers = this.popout.querySelectorAll(".iframe_wrapper");
    const locations = this.popout.querySelectorAll(".locations .location");
    wrappers.forEach((w) => w.classList.remove("is-active"));
    locations.forEach((loc) => loc.classList.remove("is-active"));
  }

  showLocation(key) {
    if (!this.iframesContainer) return;

    const wrapper = this.iframesContainer.querySelector(
      `.iframe_wrapper[data-location-key="${key}"]`
    );
    if (!wrapper) return;

    // Deactivate all wrappers and location buttons
    this.iframesContainer
      .querySelectorAll(".iframe_wrapper")
      .forEach((w) => w.classList.remove("is-active"));
    if (this.locationsContainer) {
      this.locationsContainer
        .querySelectorAll(".location")
        .forEach((loc) => loc.classList.remove("is-active"));
    }

    wrapper.classList.add("is-active");
    const locationEl = this.locationsContainer
      ? this.locationsContainer.querySelector(
          `.location[data-location-key="${key}"]`
        )
      : null;
    if (locationEl) locationEl.classList.add("is-active");

    // Fix panels that were created with hidden="null" (invalid; keeps panel hidden)
    wrapper.querySelectorAll(".meet-tabs-panel.is-active[hidden]").forEach((p) => p.removeAttribute("hidden"));

    // Lazy-load iframes: only inject when first time this location is shown
    if (!wrapper.hasAttribute("data-iframes-loaded")) {
      this.loadIframesForWrapper(wrapper);
      wrapper.setAttribute("data-iframes-loaded", "true");
    }
  }

  /**
   * Get a display name from a HubSpot meeting URL slug (e.g. charlotte-wright1 -> Charlotte Wright)
   */
  getMeetingNameFromUrl(url) {
    try {
      const pathname = new URL(url).pathname;
      const slug = pathname.replace(/^\//, "").split("?")[0];
      if (!slug) return "Book";
      const name = slug
        .split("-")
        .map((word) => {
          const cleaned = word.replace(/\d+$/, "");
          return cleaned
            ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase()
            : word;
        })
        .join(" ")
        .trim();
      return name || "Book";
    } catch {
      return "Book";
    }
  }

  /**
   * Inject HubSpot / Calendly-style iframe with loading overlay.
   * Reveal after `load` plus a short delay — embeds often reflow/jerk for a moment after first paint.
   */
  attachHubspotIframe(cell, url) {
    const postLoadSettleMs = 850;

    cell.classList.add("hubspot-embed-cell");
    cell.setAttribute("aria-busy", "true");

    const loading = document.createElement("div");
    loading.className = "hubspot-embed-loading";
    loading.setAttribute("aria-hidden", "true");
    const spinner = document.createElement("span");
    spinner.className = "hubspot-embed-spinner";
    loading.appendChild(spinner);

    const iframe = document.createElement("iframe");
    iframe.className = "hubspot-embed-iframe";
    iframe.src = url;
    iframe.setAttribute("title", "HubSpot meeting scheduler");

    cell.textContent = "";
    cell.appendChild(loading);
    cell.appendChild(iframe);

    const reveal = () => {
      if (cell.classList.contains("hubspot-embed-is-ready")) return;
      cell.classList.add("hubspot-embed-is-ready");
      cell.setAttribute("aria-busy", "false");
    };

    const fallbackMs = 20000;
    const fallbackTid = window.setTimeout(reveal, fallbackMs);
    iframe.addEventListener(
      "load",
      () => {
        window.clearTimeout(fallbackTid);
        window.setTimeout(reveal, postLoadSettleMs);
      },
      { once: true }
    );
  }

  loadIframesForWrapper(wrapper) {
    const selectors = [".hubspot_iframe_1", ".hubspot_iframe_2"];
    const cellsWithUrls = [];

    selectors.forEach((sel) => {
      const cell = wrapper.querySelector(sel);
      if (!cell) return;
      const url = (cell.textContent || "").trim();
      if (!url) return;
      this.attachHubspotIframe(cell, url);
      cellsWithUrls.push({ cell, url });
    });

    // On mobile (< 768px), wrap multiple iframes in tabs so it's clear there are two people
    if (cellsWithUrls.length >= 2) {
      this.wrapInMeetTabs(wrapper, cellsWithUrls);
    }
  }

  wrapInMeetTabs(wrapper, cellsWithUrls) {
    const meetTabs = document.createElement("div");
    meetTabs.className = "meet-tabs";

    const nav = document.createElement("div");
    nav.className = "meet-tabs-nav";
    nav.setAttribute("role", "tablist");
    nav.setAttribute("aria-label", "Choose who to meet with");

    const panelsContainer = document.createElement("div");
    panelsContainer.className = "meet-tabs-panels";

    cellsWithUrls.forEach(({ cell, url }, index) => {
      const name = this.getMeetingNameFromUrl(url);
      const tabId = `meet-tab-${wrapper.getAttribute("data-location-key")}-${index}`;
      const panelId = `meet-panel-${wrapper.getAttribute("data-location-key")}-${index}`;

      const button = document.createElement("button");
      button.type = "button";
      button.className = "meet-tabs-trigger" + (index === 0 ? " is-active" : "");
      button.setAttribute("role", "tab");
      button.setAttribute("aria-selected", index === 0 ? "true" : "false");
      button.setAttribute("aria-controls", panelId);
      button.setAttribute("id", tabId);
      button.textContent = `Meet with ${name}`;
      nav.appendChild(button);

      const panel = document.createElement("div");
      panel.className = "meet-tabs-panel" + (index === 0 ? " is-active" : "");
      panel.setAttribute("role", "tabpanel");
      panel.setAttribute("id", panelId);
      panel.setAttribute("aria-labelledby", tabId);
      if (index !== 0) panel.setAttribute("hidden", "");
      panel.appendChild(cell);
      panelsContainer.appendChild(panel);
    });

    meetTabs.appendChild(nav);
    meetTabs.appendChild(panelsContainer);
    wrapper.appendChild(meetTabs);

    nav.addEventListener("click", (e) => {
      const trigger = e.target.closest(".meet-tabs-trigger");
      if (!trigger || !meetTabs.contains(trigger)) return;
      const index = Array.from(nav.querySelectorAll(".meet-tabs-trigger")).indexOf(trigger);
      if (index < 0) return;

      nav.querySelectorAll(".meet-tabs-trigger").forEach((t, i) => {
        t.classList.toggle("is-active", i === index);
        t.setAttribute("aria-selected", i === index ? "true" : "false");
      });
      panelsContainer.querySelectorAll(".meet-tabs-panel").forEach((p, i) => {
        const isActive = i === index;
        p.classList.toggle("is-active", isActive);
        p.hidden = !isActive;
      });
    });
  }
}

export default ConsultationPopout;
